import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Eye, EyeOff, LogIn, Sparkles, Users, Building2, Crown } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const demoAccounts = [
    { 
      id: '100001', 
      password: '2026', 
      name: 'John Smith', 
      role: 'Employee',
      icon: Users,
      color: 'bg-blue-500',
      description: 'View personal KPI, payslips, training'
    },
    { 
      id: '200001', 
      password: 'admin2026', 
      name: 'Sarah Johnson', 
      role: 'HR Manager (Supervisor)',
      icon: Building2,
      color: 'bg-violet-500',
      description: 'Monitor employees, manage payroll, view recordings'
    },
    { 
      id: '300001', 
      password: 'exec2026', 
      name: 'Michael Chen', 
      role: 'CEO (Executive)',
      icon: Crown,
      color: 'bg-amber-500',
      description: 'Company analytics, AI insights, full access'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!employeeId || !password) {
      toast.error('Please enter employee ID and password');
      return;
    }

    setIsLoading(true);
    const result = await login(employeeId, password);
    setIsLoading(false);

    if (result.success) {
      toast.success(`Welcome back, ${result.user.first_name}!`);
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  const handleDemoLogin = async (account) => {
    setEmployeeId(account.id);
    setPassword(account.password);
    setIsLoading(true);
    
    const result = await login(account.id, account.password);
    setIsLoading(false);

    if (result.success) {
      toast.success(`Welcome to Qfinity, ${result.user.first_name}!`);
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative flex flex-col lg:flex-row gap-6 p-4 max-w-5xl">
        {/* Login Form */}
        <Card className="login-card relative flex-1" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              Qfinity AI Workforce
            </CardTitle>
            <CardDescription className="text-slate-500">
              World's First HR Intelligence Platform
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employeeId" className="text-slate-700 font-medium">
                  Employee ID
                </Label>
                <Input
                  id="employeeId"
                  data-testid="employee-id-input"
                  type="text"
                  placeholder="Enter your employee ID"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 bg-slate-50 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={isLoading}
                className="w-full h-11 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium shadow-lg shadow-indigo-500/25"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="spinner w-4 h-4 border-2 border-white/30 border-t-white" />
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Demo Accounts */}
        <Card className="relative flex-1 lg:max-w-md" data-testid="demo-accounts-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Sparkles className="w-5 h-5 text-violet-500" />
              Demo Accounts
            </CardTitle>
            <CardDescription>
              Choose a role to explore the platform
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {demoAccounts.map((account) => (
              <button
                key={account.id}
                onClick={() => handleDemoLogin(account)}
                disabled={isLoading}
                className="w-full p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 transition-all text-left group"
                data-testid={`demo-login-${account.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 ${account.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <account.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                      {account.name}
                    </p>
                    <p className="text-sm text-indigo-600 font-medium">
                      {account.role}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {account.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <span className="font-mono bg-slate-200 px-1.5 py-0.5 rounded">ID: {account.id}</span>
                      <span className="font-mono bg-slate-200 px-1.5 py-0.5 rounded">Pass: {account.password}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
